import { useEffect, useRef } from 'react';
import type { HoldList, Hold, PatchedHoldCreateUpdate } from 'api-client/models';
import { ResponseError } from 'api-client/runtime';
import { useAppContext } from '@/context/AppContext';
import type { ExpenseItem } from '../types/ExpenseItem';
import { isHoldDetailSelectable } from '../utils/holdSelection';

type TimingFields = Pick<PatchedHoldCreateUpdate,
  'loadInTime' | 'soundcheckTime' | 'doorsTime' | 'showTime' | 'endTime'>;

export interface BulkCopyResult {
  success: boolean;
  copiedHoldIds: string[];
  failedHoldIds: string[];
  uncertainHoldIds: string[];
  error: string | null;
  cancelled?: boolean;
}

export const useBulkOperations = (
  setInfoMessage: (message: string | null) => void,
  setError: (error: string | null) => void,
  refetch: () => Promise<void>,
  scope: { showId?: string; holds: HoldList[] },
) => {
  const { apiClients } = useAppContext();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const currentShow = useRef({ id: scope.showId });
  if (currentShow.current.id !== scope.showId) currentShow.current = { id: scope.showId };
  const version = currentShow.current;
  const currentHolds = useRef(scope.holds);
  currentHolds.current = scope.holds;

  const failure = (message: string): BulkCopyResult => {
    setError(message);
    return { success: false, copiedHoldIds: [], failedHoldIds: [], uncertainHoldIds: [], error: message };
  };
  const cancelled = (
    copiedHoldIds: string[] = [], failedHoldIds: string[] = [], uncertainHoldIds: string[] = [],
  ): BulkCopyResult => ({
    success: false, copiedHoldIds, failedHoldIds, uncertainHoldIds,
    error: 'Show changed during the copy operation.', cancelled: true,
  });
  const ensureTarget = (holdId: string): void => {
    if (!mounted.current || currentShow.current !== version || !currentHolds.current.some((hold) =>
      hold.id === holdId && hold.show === version.id && hold.status === 'confirmed')) {
      throw new Error('Date is no longer a current confirmed target.');
    }
  };

  const copyToDates = async (
    kind: 'Times' | 'Expenses',
    selectedHold: HoldList,
    allHolds: HoldList[],
    update: (hold: HoldList, markAttempted: () => void) => Promise<void>,
  ): Promise<BulkCopyResult> => {
    if (!mounted.current || currentShow.current !== version) return cancelled();
    setInfoMessage(null);
    setError(null);
    const source = currentHolds.current.find((hold) =>
      hold.id === selectedHold.id && hold.show === version.id && isHoldDetailSelectable(hold));
    if (!source) return failure('Select a current source date before copying.');
    const candidateIds = new Set(allHolds.map((hold) => hold.id));
    const targets = currentHolds.current.filter((hold) => candidateIds.has(hold.id)
      && hold.id !== source.id && hold.show === source.show && hold.status === 'confirmed');
    if (targets.length === 0) {
      setInfoMessage(`No other dates to copy ${kind.toLowerCase()} to`);
      return { success: true, copiedHoldIds: [], failedHoldIds: [], uncertainHoldIds: [], error: null };
    }
    const attemptedIds = new Set<string>();
    const outcomes = await Promise.allSettled(targets.map((hold) => update(hold, () => { attemptedIds.add(hold.id); })));
    const copiedHoldIds = targets.filter((_, index) => outcomes[index].status === 'fulfilled').map((hold) => hold.id);
    const failedHoldIds = targets.filter((_, index) => outcomes[index].status === 'rejected').map((hold) => hold.id);
    const uncertainHoldIds = targets.filter((hold, index) => {
      const outcome = outcomes[index];
      if (outcome.status !== 'rejected' || !attemptedIds.has(hold.id)) return false;
      const reason: unknown = outcome.reason;
      return !(reason instanceof ResponseError && [400, 401, 403, 404, 405, 415, 422, 429].includes(reason.response.status));
    }).map((hold) => hold.id);
    if (!mounted.current || currentShow.current !== version) return cancelled(copiedHoldIds, failedHoldIds, uncertainHoldIds);
    const failures = outcomes.flatMap((outcome, index) => {
      if (outcome.status === 'fulfilled') return [];
      const reason: unknown = outcome.reason;
      return [`${targets[index].id}: ${reason instanceof Error ? reason.message : 'Update failed.'}`];
    });
    if (copiedHoldIds.length > 0) {
      setInfoMessage(`${kind} copied to ${copiedHoldIds.length} other date${copiedHoldIds.length > 1 ? 's' : ''}`);
    }
    if (uncertainHoldIds.length > 0) {
      failures.push(`Unknown write outcome for ${uncertainHoldIds.join(', ')}. Refresh and inspect before appending again.`);
    }
    if (attemptedIds.size > 0) {
      try {
        await refetch();
      } catch {
        failures.push('Could not refresh show data. Reload to verify copied values.');
      }
    }
    if (!mounted.current || currentShow.current !== version) return cancelled(copiedHoldIds, failedHoldIds, uncertainHoldIds);
    const message = failures.length ? `Failed to finish copying ${kind.toLowerCase()}. ${failures.join(' ')}` : null;
    setError(message);
    return { success: message === null, copiedHoldIds, failedHoldIds, uncertainHoldIds, error: message };
  };

  const copyTimesToAllDates = async (
    selectedHold: HoldList,
    allHolds: HoldList[],
    formData: TimingFields & { [key: string]: unknown },
  ): Promise<BulkCopyResult> => {
    if (!mounted.current || currentShow.current !== version) return cancelled();
    if (![formData.loadInTime, formData.soundcheckTime, formData.doorsTime, formData.showTime, formData.endTime].some(Boolean)) {
      setInfoMessage(null);
      return failure('No time fields to copy');
    }
    return copyToDates('Times', selectedHold, allHolds, async (hold, markAttempted) => {
      ensureTarget(hold.id);
      markAttempted();
      await apiClients.holds.holdsPartialUpdate({
        id: hold.id,
        patchedHoldCreateUpdate: {
          loadInTime: formData.loadInTime,
          soundcheckTime: formData.soundcheckTime,
          doorsTime: formData.doorsTime,
          showTime: formData.showTime,
          endTime: formData.endTime,
        },
      });
    });
  };

  const copyExpensesToAllDates = async (
    selectedHold: HoldList,
    allHolds: HoldList[],
    formData: { expenseItems?: ExpenseItem[]; [key: string]: unknown },
    fetchFullHoldDetails: (holdId: string) => Promise<Hold | null>,
  ): Promise<BulkCopyResult> => {
    if (!mounted.current || currentShow.current !== version) return cancelled();
    const expenses = formData.expenseItems;
    if (!expenses?.length) {
      setInfoMessage(null);
      return failure('No expense items to copy');
    }
    return copyToDates('Expenses', selectedHold, allHolds, async (hold, markAttempted) => {
      const details = await fetchFullHoldDetails(hold.id);
      if (!details || details.id !== hold.id || details.show !== version.id) {
        throw new Error('Could not retrieve current expenses; no update was sent.');
      }
      const existing: unknown = details.expenseItems;
      if (existing != null && !Array.isArray(existing)) {
        throw new Error('Existing expenses are invalid; no update was sent.');
      }
      ensureTarget(hold.id);
      markAttempted();
      await apiClients.holds.holdsPartialUpdate({
        id: hold.id,
        patchedHoldCreateUpdate: { expenseItems: [...(Array.isArray(existing) ? existing : []), ...expenses] },
      });
    });
  };

  return { copyTimesToAllDates, copyExpensesToAllDates };
};
